/* @flow */

import { StorageKeys } from '../localStorage/keys';
import LocalStorageManager from '../localStorage/localStorageManager';

type MotionListener = (isReduced: boolean) => void;

class ReducedMotionManager {
  listeners: Set<MotionListener>;
  mediaQueryList: MediaQueryList | null;

  constructor() {
    this.listeners = new Set();
    this.mediaQueryList = null;
  }

  initialize() {
    if (typeof window === 'undefined') {
      return;
    }

    const matchMedia = 'matchMedia' in window ? (window as Object).matchMedia : null;

    if (typeof matchMedia === 'function') {
      // Handle user's OS preference
      this.mediaQueryList = matchMedia('(prefers-reduced-motion: reduce)');

      const handleSystemPreferenceChange = (event: MediaQueryListEvent) => {
        if (!LocalStorageManager.hasKey(StorageKeys.PrefersReducedMotion)) {
          this.applyPreference(event.matches);
        }
      };

      if (this.mediaQueryList) {
        this.mediaQueryList.addEventListener('change', handleSystemPreferenceChange);
      }
    }

    // Apply initial state from LocalStorage or OS preference
    const isReduced = this.isReduced();
    this.applyPreference(isReduced);
  }

  // Return saved setting from local storage if present, else return user's system setting
  isReduced(): boolean {
    return LocalStorageManager.loadBoolean(StorageKeys.PrefersReducedMotion, Boolean(this.mediaQueryList?.matches));
  }

  toggle(): boolean {
    const nextState = !this.isReduced();
    LocalStorageManager.save(StorageKeys.PrefersReducedMotion, nextState);
    this.applyPreference(nextState);
    return nextState;
  }

  applyPreference(isReduced: boolean) {
    if (document.body) {
      document.body.classList.toggle('reduceMotion', isReduced);
    }
    this.listeners.forEach((listener) => listener(isReduced));
  }

  subscribe(listener: MotionListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }
}

const instance: ReducedMotionManager = new ReducedMotionManager();
export default instance;
